/**
 * Store, article and size names beside the codes of a move run's lines — from the reads that
 * already name them; nothing is guessed.
 *
 * A move line carries keys only (`origin_store_key`, `destination_store_key`, `article_key`,
 * `size_key`). The run names the in-season run its keys are named from (`names_run_id`, bata-platform
 * #942), and that run already names every key it holds. It is NOT `candidate_run_id`: a run that
 * solved its own full-price candidates (platform ADR 9056) records its own move-run id there, which
 * no in-season read knows, and every name read «not reported» (2026-11-02).
 *
 *   stores, articles  `GET /inseason/runs/{run_id}/names` (bata-platform ADR 6177), through
 *                     `useRunNames` — the same names every in-season screen shows;
 *   sizes             `GET /inseason/runs/{run_id}/network-health/sizes`, one store × article: every
 *                     size the run's stock holds for the article at any store, each with its code,
 *                     scale and position (ADR 2006's size name).
 *
 * A run with no names run (no distribute staged its batch) names nothing: every key shows its code and
 * the size says it is not named, as `sizeName` says for any read that publishes none.
 */

import { useMemo } from 'react';

import { BACKGROUND_READ, callOperation, SESSION_LONG_READ, useApiQueries } from '@/api';
import { networkHealthSizes, type MoveListItem, type NetworkHealthSizes } from '@/api/generated';
import { sizeName, sizeNameAliased, type SizeName } from '@/planning';

/** One article's sizes by key, as the network-health read names them. */
export type SizeIndex = ReadonlyMap<string, SizeName>;

/** The distinct (store, article) pairs whose sizes a set of lines needs: one per article, its first origin. Pure. */
export function sizeAsks(lines: readonly Pick<MoveListItem, 'article_key' | 'origin_store_key' | 'size_key'>[]): readonly { readonly article: string; readonly store: string }[] {
  const seen = new Map<string, string>();
  for (const line of lines) {
    if (line.article_key === null || line.origin_store_key === null || line.size_key === null) continue;
    if (!seen.has(line.article_key)) seen.set(line.article_key, line.origin_store_key);
  }
  return [...seen.entries()].map(([article, store]) => ({ article, store }));
}

/** Index the network-health answers by article, then size key. Pure. */
export function indexSizes(answers: readonly NetworkHealthSizes[]): ReadonlyMap<string, SizeIndex> {
  const out = new Map<string, Map<string, SizeName>>();
  for (const answer of answers) {
    const sizes = out.get(answer.articleKey) ?? new Map<string, SizeName>();
    for (const size of answer.sizes) sizes.set(size.sizeKey, sizeNameAliased(size));
    out.set(answer.articleKey, sizes);
  }
  return out;
}

/** A line's size as named, or its key with no name (the read did not name it). Pure. */
export function lineSize(index: ReadonlyMap<string, SizeIndex>, line: Pick<MoveListItem, 'article_key' | 'size_key'>): SizeName {
  const key = line.size_key ?? '';
  const named = line.article_key === null ? undefined : index.get(line.article_key)?.get(key);
  return named ?? sizeName({ size_key: key, size_code: null, size_scale: null, size_run_position: null, size_absent_reason: null });
}

/** The sizes of the lines a screen draws, read from the names run, one request per article. */
export function useMoveSizeNames(
  namesRunId: string | null,
  lines: readonly Pick<MoveListItem, 'article_key' | 'origin_store_key' | 'size_key'>[],
): (line: Pick<MoveListItem, 'article_key' | 'size_key'>) => SizeName {
  const asks = useMemo(() => sizeAsks(lines), [lines]);
  const answers = useApiQueries(
    networkHealthSizes,
    asks.map((ask) => ({ path: { run_id: namesRunId ?? '' }, query: { store_key: ask.store, article_key: ask.article } })),
    { enabled: namesRunId !== null, ...SESSION_LONG_READ, ...BACKGROUND_READ },
  );
  const signature = answers.map((read) => `${read.status}:${read.dataUpdatedAt}`).join('|');
  const index = useMemo(
    () => indexSizes(answers.flatMap((read) => (read.data === undefined ? [] : [read.data]))),
    // `signature` stands for the answers' data: a new answer is a new index.
    [signature],
  );
  return (line) => lineSize(index, line);
}

/** The sizes of a file's lines, gathered while its lists are still arriving. */
export interface MoveSizeNamer {
  /** Lines the file will hold: their articles' sizes are asked for now, while the other lists arrive. */
  readonly add: (lines: readonly Pick<MoveListItem, 'article_key' | 'origin_store_key' | 'size_key'>[]) => void;
  /** Every article's sizes, once every ask has answered. */
  readonly finish: () => Promise<ReadonlyMap<string, SizeIndex>>;
}

type SizeLine = Pick<MoveListItem, 'article_key' | 'origin_store_key' | 'size_key'>;

/**
 * The same outside React, for a file: a few requests at a time; a failed read names nothing.
 *
 * **A size is asked for until it is named, not once per article** (owner report 2026-11-02: the
 * Colombian run's file asked once for each of its ~1,500 articles, six at a time — some 250 requests
 * in series). The platform names a size from its key alone (bata-platform `size_names`: the size
 * dimension in force, by `size_key`, the same lookup for every sized read), and each network-health
 * answer names every size its article holds anywhere — so the answer for one article of a scale names
 * the sizes of the next. An article is asked for only while one of its lines' sizes is still unnamed;
 * an article whose sizes another answer already named takes them from there: the same names, read once.
 * A size no answer names keeps its article's ask, exactly as before.
 */
export function moveSizeNamer(namesRunId: string | null, concurrency = 6): MoveSizeNamer {
  /** Each article: the store its first line leaves from (the store × article the platform reads), and the sizes its lines name. */
  const articles = new Map<string, { readonly store: string; readonly sizes: Set<string> }>();
  const named = new Map<string, SizeName>();
  const answers = new Map<string, SizeIndex>();
  const asked = new Set<string>();
  const queued = new Set<string>();
  const queue: string[] = [];
  const running = new Set<Promise<void>>();
  const unnamed = (article: string): boolean => [...articles.get(article)!.sizes].some((size) => !named.has(size));

  const worker = async (): Promise<void> => {
    for (let article = queue.shift(); article !== undefined; article = queue.shift()) {
      queued.delete(article);
      // Named by an answer that came in since the article was queued: nothing left to ask.
      if (asked.has(article) || !unnamed(article)) continue;
      asked.add(article);
      try {
        const answer = await callOperation(networkHealthSizes, {
          path: { run_id: namesRunId! }, query: { store_key: articles.get(article)!.store, article_key: article },
        });
        for (const [answered, sizes] of indexSizes([answer])) {
          answers.set(answered, sizes);
          for (const [key, size] of sizes) named.set(key, size);
        }
      } catch {
        // A size the read could not name keeps its key; the rest of the file is unaffected.
      }
    }
  };
  const pump = (): void => {
    while (running.size < Math.max(1, concurrency) && queue.length > 0) {
      const one = worker().finally(() => { running.delete(one); });
      running.add(one);
    }
  };

  return {
    add: (lines: readonly SizeLine[]) => {
      if (namesRunId === null) return;
      for (const line of lines) {
        if (line.article_key === null || line.origin_store_key === null || line.size_key === null) continue;
        const known = articles.get(line.article_key) ?? { store: line.origin_store_key, sizes: new Set<string>() };
        articles.set(line.article_key, known);
        known.sizes.add(line.size_key);
        if (!named.has(line.size_key) && !asked.has(line.article_key) && !queued.has(line.article_key)) {
          queued.add(line.article_key);
          queue.push(line.article_key);
        }
      }
      pump();
    },
    finish: async () => {
      while (running.size > 0) await Promise.all([...running]);
      const out = new Map<string, SizeIndex>();
      for (const [article, { sizes }] of articles) {
        const own = answers.get(article);
        const index = new Map(own ?? []);
        for (const key of sizes) {
          const size = named.get(key);
          if (!index.has(key) && size !== undefined) index.set(key, size);
        }
        if (own !== undefined || index.size > 0) out.set(article, index);
      }
      return out;
    },
  };
}

/** Every article's sizes for a set of lines, outside React. */
export async function readMoveSizeNames(namesRunId: string | null, lines: readonly SizeLine[], concurrency = 6): Promise<ReadonlyMap<string, SizeIndex>> {
  const namer = moveSizeNamer(namesRunId, concurrency);
  namer.add(lines);
  return namer.finish();
}
